import { useCallback, useEffect, useMemo, useState, type JSX } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { useAuth0AccessToken } from "@/hooks/useAuth0AccessToken"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import PersonalInformation, {
  type PersonalInformationCandidate,
} from "@/components/personal-information"
import { Button } from "@/components/ui/button"
import { ButtonGroup } from "@/components/ui/button-group"
import { ChevronLeft, ChevronRight, ExternalLink, FileDown, Undo2 } from "lucide-react"
import CandidateSidebar, { type CandidateVacancyAssignment } from "@/components/candidate-sidebar"
import type { Database } from "@/types/supabase"
import type { KanbanFeature } from "@/types/kanban"
import {
  buildStageAssociationLookup,
  type CandidateStatusId,
  type StageAssociationLookup,
} from "@/types/candidate"
import { supabase } from "@/lib/functions/supabase/supabase"
import { mapApplicationsToKanbanFeatures, normalizeHubspotId } from "@/lib/kanban-data"
import Timeline, { type TimelineItem } from "@/components/timeline"
import { useCandidateStatusUpdater } from "@/hooks/useCandidateStatusUpdater"
import CandidateVacancyBanner from "@/components/candidate-vacancy-banner"
import CandidateAttachments from "@/components/candidate-attachments"
import { useCandidateApplication } from "@/hooks/supabase/useCandidateApplication"
import { useContactAttachments } from "@/hooks/supabase/useContactAttachments"
import { useSetApplicationTags } from "@/hooks/supabase/useSetApplicationTags"
import { combineAssociationTags, slugifyTagLabel } from "@/lib/tag-utils"

type ApplicationRow = Database["public"]["Tables"]["applications"]["Row"]

type CandidateLocationState = {
  candidate?: KanbanFeature
  applicationId?: string | number | null
  vacancyId?: string | null
  vacancyTitle?: string | null
  companyName?: string | null
  vacancySlug?: string | null
  originView?: "kanban" | "table"
  stageAssociations?: StageAssociationLookup
}

type CandidateMappingResult = {
  info: PersonalInformationCandidate
  fullName: string
}

const buildFullName = (
  firstName?: string | null,
  lastName?: string | null,
  fallback?: string | null
) => {
  const parts = [firstName, lastName].filter((part): part is string => Boolean(part))
  const joined = parts.join(" ")
  return joined || fallback || "Onbekende kandidaat"
}

const mapFromKanbanFeature = (candidate: KanbanFeature): CandidateMappingResult => ({
  info: {
    firstName: candidate.firstName,
    lastName: candidate.lastName,
    phone: candidate.phone ?? null,
    email: candidate.email,
    birthDate: candidate.birthDate ?? null,
    city: candidate.city ?? null,
    street: candidate.street ?? null,
    postalCode: candidate.postalCode ?? null,
    resumeUrl: candidate.resumeUrl ?? null,
    hubspotUrl: candidate.hubspotUrl ?? null,
  },
  fullName: buildFullName(candidate.firstName, candidate.lastName, candidate.name),
})

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null

const extractString = (payload: unknown, key: string): string | null => {
  if (!isRecord(payload)) {
    return null
  }
  const value = payload[key]
  return typeof value === "string" && value.trim().length > 0 ? value : null
}

const DEFAULT_STATUS: CandidateStatusId = "sollicitanten"

const Kandidaat = () => {
  const navigate = useNavigate()
  const params = useParams<{ candidateId: string; vacancyId?: string }>()
  const candidateId = params.candidateId
  const vacancyParam = params.vacancyId ?? null
  const location = useLocation()
  const locationState = location.state as CandidateLocationState | undefined
  const { user } = useAuth0()
  const getAccessToken = useAuth0AccessToken()
  const { setApplicationTags } = useSetApplicationTags()
  const fallbackActorEmail = "system@unknown.local"
  const fallbackActorName = "System"
  const actorEmail = user?.email ?? fallbackActorEmail
  const actorName = user?.nickname ?? user?.name ?? fallbackActorName

  const [vacancyTitle, setVacancyTitle] = useState<string | null>(
    locationState?.vacancyTitle ?? null
  )
  const [vacancyCompany, setVacancyCompany] = useState<string | null>(
    locationState?.companyName ?? locationState?.candidate?.owner?.name ?? null
  )

  const candidateFromState = useMemo(() => {
    if (!locationState?.candidate) {
      return null
    }
    return mapFromKanbanFeature(locationState.candidate)
  }, [locationState])

  const stageAssociationsFromState = useMemo<StageAssociationLookup>(
    () => locationState?.stageAssociations ?? {},
    [locationState?.stageAssociations]
  )

  const [stageAssociations, setStageAssociations] = useState<StageAssociationLookup>(
    stageAssociationsFromState
  )

  useEffect(() => {
    setStageAssociations(stageAssociationsFromState)
  }, [stageAssociationsFromState])

  const applicationIdFromState =
    locationState?.applicationId ?? locationState?.candidate?.applicationId ?? null

  const [activeFeature, setActiveFeature] = useState<KanbanFeature | null>(
    locationState?.candidate ?? null
  )
  const [status, setStatus] = useState<CandidateStatusId>(
    locationState?.candidate?.column ?? DEFAULT_STATUS
  )
  const [statusSaving, setStatusSaving] = useState(false)
  const [statusError, setStatusError] = useState<string | null>(null)
  const [manualTagsSaving, setManualTagsSaving] = useState(false)
  const [manualTagsError, setManualTagsError] = useState<string | null>(null)
  const { syncStatus } = useCandidateStatusUpdater()

  const [candidate, setCandidate] = useState<PersonalInformationCandidate | null>(
    candidateFromState?.info ?? null
  )
  const [fullName, setFullName] = useState<string>(
    candidateFromState?.fullName ?? "Kandidaat"
  )
  const [loading, setLoading] = useState<boolean>(!candidateFromState)
  const [error, setError] = useState<string | null>(null)
  const [applicationContext, setApplicationContext] = useState<{
    vacancyId: string | null
    companyId: string | null
  } | null>(null)

  useEffect(() => {
    if (!locationState?.candidate) {
      return
    }
    setActiveFeature(locationState.candidate)
    setStatus(locationState.candidate.column)
    setStatusError(null)
  }, [locationState?.candidate])

  useEffect(() => {
    if (!locationState?.vacancyTitle) {
      return
    }
    setVacancyTitle(locationState.vacancyTitle)
  }, [locationState?.vacancyTitle])

  useEffect(() => {
    const ownerName =
      locationState?.companyName ?? locationState?.candidate?.owner?.name ?? null
    if (!ownerName) {
      return
    }
    setVacancyCompany(ownerName)
  }, [locationState?.companyName, locationState?.candidate])

  useEffect(() => {
    if (!activeFeature?.owner?.name) {
      return
    }
    setVacancyCompany((current) => current ?? activeFeature.owner?.name ?? null)
  }, [activeFeature?.owner?.name])

  const fallbackVacancyId = useMemo(
    () =>
      normalizeHubspotId(
        locationState?.vacancyId ??
          vacancyParam ??
          activeFeature?.vacancyId ??
          applicationContext?.vacancyId ??
          null
      ),
    [
      locationState?.vacancyId,
      vacancyParam,
      activeFeature?.vacancyId,
      applicationContext?.vacancyId,
    ]
  )

  const backDestination = useMemo(
    () => locationState?.vacancySlug ?? vacancyParam ?? locationState?.vacancyId ?? fallbackVacancyId,
    [locationState?.vacancySlug, vacancyParam, locationState?.vacancyId, fallbackVacancyId]
  )

  const candidateHubspotId = useMemo(
    () =>
      normalizeHubspotId(
        locationState?.candidate?.contactId ??
        locationState?.candidate?.id ??
        candidateId ??
        null
      ),
    [locationState?.candidate?.contactId, locationState?.candidate?.id, candidateId]
  )

  const vacancyHubspotId = useMemo(
    () =>
      normalizeHubspotId(
        locationState?.vacancyId ??
        vacancyParam ??
        activeFeature?.vacancyId ??
        fallbackVacancyId
      ),
    [locationState?.vacancyId, vacancyParam, activeFeature?.vacancyId, fallbackVacancyId]
  )

  const companyHubspotId = useMemo(
    () =>
      normalizeHubspotId(
        locationState?.candidate?.owner?.id ??
          activeFeature?.owner?.id ??
          applicationContext?.companyId ??
          null
      ),
    [locationState?.candidate?.owner?.id, activeFeature?.owner?.id, applicationContext?.companyId]
  )

  useEffect(() => {
    if (candidateFromState) {
      return
    }

    if (!candidateId) {
      setError("Geen kandidaat-id opgegeven.")
      setLoading(false)
      return
    }

    if (!candidateHubspotId) {
      setError("Kandidaat-id is ongeldig.")
      setLoading(false)
      return
    }

    setError(null)
  }, [candidateFromState, candidateId, candidateHubspotId])

  useEffect(() => {
    if (!candidateHubspotId) {
      return
    }

    const hasVacancy = Boolean(vacancyHubspotId)
    const hasCompany = Boolean(companyHubspotId)

    if (hasVacancy && hasCompany) {
      return
    }

    let isCancelled = false

    const fetchApplicationContext = async () => {
      try {
        let query = supabase
          .from("applications")
          .select("vacancy_id,company_id,last_synced_at")
          .eq("candidate_id", candidateHubspotId)
          .order("last_synced_at", { ascending: false })
          .limit(1)

        if (hasVacancy && vacancyHubspotId) {
          query = query.eq("vacancy_id", vacancyHubspotId)
        }

        const { data, error } = await query.maybeSingle<
          Pick<ApplicationRow, "vacancy_id" | "company_id">
        >()

        if (isCancelled) {
          return
        }

        if (error) {
          console.warn("[candidate] fallback application lookup failed:", error)
          if (!candidateFromState) {
            setError("Kon de vacaturecontext van deze kandidaat niet laden.")
          }
          setLoading(false)
          return
        }

        if (!data) {
          if (!candidateFromState) {
            setError("Geen vacaturecontext gevonden voor deze kandidaat.")
          }
          setLoading(false)
          return
        }

        setError(null)
        setApplicationContext((current) => {
          const nextVacancy = normalizeHubspotId(data.vacancy_id)
          const nextCompany = normalizeHubspotId(data.company_id)
          if (
            current?.vacancyId === nextVacancy &&
            current?.companyId === nextCompany
          ) {
            return current
          }
          return {
            vacancyId: nextVacancy,
            companyId: nextCompany,
          }
        })
      } catch (err) {
        if (isCancelled) {
          return
        }
        console.warn("[candidate] fallback application lookup threw:", err)
        if (!candidateFromState) {
          setError("Kon de vacaturecontext van deze kandidaat niet laden.")
        }
        setLoading(false)
      }
    }

    fetchApplicationContext()

    return () => {
      isCancelled = true
    }
  }, [
    candidateFromState,
    candidateHubspotId,
    vacancyHubspotId,
    companyHubspotId,
  ])

  const {
    data: candidateApplicationData,
    loading: candidateApplicationLoading,
    error: candidateApplicationError,
  } = useCandidateApplication(candidateHubspotId, vacancyHubspotId, companyHubspotId)
  const {
    data: contactAttachmentsData,
    loading: contactAttachmentsLoading,
    error: contactAttachmentsError,
    refetch: refetchContactAttachments,
  } = useContactAttachments(candidateHubspotId)

  const statusHistoryContext = useMemo(
    () => ({
      vacancyName: vacancyTitle,
      vacancyTitle,
      vacancyLocation: candidateApplicationData?.vacancy?.locatie ?? null,
      vacancyDiscipline: candidateApplicationData?.vacancy?.vakgebied ?? null,
      companyName: vacancyCompany,
    }),
    [
      vacancyTitle,
      candidateApplicationData?.vacancy?.locatie,
      candidateApplicationData?.vacancy?.vakgebied,
      vacancyCompany,
    ]
  )

  useEffect(() => {
    const labels = candidateApplicationData?.associationLabels ?? []
    if (!labels || labels.length === 0) {
      return
    }
    const lookup = buildStageAssociationLookup(labels)
    setStageAssociations((current) => {
      let changed = false
      const next: StageAssociationLookup = { ...current }
      for (const [stageId, association] of Object.entries(lookup)) {
        const key = stageId as keyof StageAssociationLookup
        const existing = current[key]
        if (
          !existing ||
          existing.typeId !== association.typeId ||
          existing.label !== association.label ||
          existing.slug !== association.slug
        ) {
          next[key] = association
          changed = true
        }
      }
      return changed ? next : current
    })
  }, [candidateApplicationData?.associationLabels])

  const candidateApplicationFeatures = useMemo(() => {
    if (!candidateApplicationData?.applications?.length) {
      return []
    }
    const fallbackVacancy =
      candidateApplicationData.vacancy?.id ?? vacancyHubspotId ?? fallbackVacancyId
    return mapApplicationsToKanbanFeatures(candidateApplicationData.applications, candidateApplicationData.company, {
      fallbackVacancyId: fallbackVacancy,
      stageAssociations,
    })
  }, [
    candidateApplicationData?.applications,
    candidateApplicationData?.company,
    candidateApplicationData?.vacancy?.id,
    vacancyHubspotId,
    fallbackVacancyId,
    stageAssociations,
  ])

  const vacancySlug = useMemo(() => {
    if (locationState?.vacancySlug) {
      return locationState.vacancySlug
    }
    if (vacancyParam) {
      return vacancyParam
    }
    if (locationState?.vacancyId) {
      return locationState.vacancyId
    }
    if (activeFeature?.vacancyId) {
      return activeFeature.vacancyId
    }
    if (candidateApplicationData?.vacancy?.id) {
      return candidateApplicationData.vacancy.id
    }
    return fallbackVacancyId
  }, [
    locationState?.vacancySlug,
    vacancyParam,
    locationState?.vacancyId,
    activeFeature?.vacancyId,
    candidateApplicationData?.vacancy?.id,
    fallbackVacancyId,
  ])

  const handleStatusChange = useCallback(
    async (nextStatus: CandidateStatusId) => {
      if (!activeFeature) {
        setStatusError("Geen vacaturecontext gevonden om de status te wijzigen.")
        return
      }

      if (statusSaving || nextStatus === status) {
        return
      }

      setStatusSaving(true)
      setStatusError(null)

      const updatedFeature: KanbanFeature = {
        ...activeFeature,
        column: nextStatus,
      }

      try {
        const result = await syncStatus({
          feature: updatedFeature,
          nextStatus,
          previousFeature: activeFeature,
          fallbackVacancyId,
          stageAssociations,
          historyContext: statusHistoryContext,
        })

        if (result.status === "fulfilled") {
          setStatus(nextStatus)
          setActiveFeature({
            ...updatedFeature,
            statusLabel: result.appliedLabel,
            associationTypeId:
              result.associationTypeId ??
              stageAssociations[nextStatus]?.typeId ??
              updatedFeature.associationTypeId ??
              null,
          })
          return
        }

        const reason =
          result.status === "skipped"
            ? result.reason.message
            : result.reason instanceof Error
              ? result.reason.message
              : "Onbekende fout bij bijwerken van status."
        setStatusError(reason)
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Onbekende fout bij bijwerken van status."
        setStatusError(message)
      } finally {
        setStatusSaving(false)
      }
    },
    [
      activeFeature,
      fallbackVacancyId,
      stageAssociations,
      status,
      statusSaving,
      statusHistoryContext,
      syncStatus,
    ]
  )
  const handleManualTagsSave = useCallback(
    async (nextTags: string[]) => {
      if (!candidateHubspotId || !vacancyHubspotId) {
        setManualTagsError("Geen kandidaat- of vacature-id gevonden voor deze context.")
        return
      }
      setManualTagsSaving(true)
      setManualTagsError(null)
      try {
        await setApplicationTags({
          vacancyId: vacancyHubspotId,
          contactId: candidateHubspotId,
          companyId: companyHubspotId,
          tags: nextTags,
          actorEmail,
          actorName,
          historyContext: {
            candidateName: fullName,
            candidateEmail: candidate?.email ?? null,
            candidatePhone: candidate?.phone ?? null,
            vacancyName: vacancyTitle,
            vacancyTitle,
            vacancyLocation: candidateApplicationData?.vacancy?.locatie ?? null,
            vacancyDiscipline: candidateApplicationData?.vacancy?.vakgebied ?? null,
            companyName: vacancyCompany,
          },
        })
        setActiveFeature((current) =>
          current
            ? {
                ...current,
                associationTags: nextTags.map((label) => ({
                  label,
                  slug: slugifyTagLabel(label),
                })),
              }
            : current
        )
        setLocalManualTags(nextTags)
      } catch (error) {
        setManualTagsError(
          error instanceof Error
            ? error.message
            : "Onbekende fout bij het opslaan van tags."
        )
      } finally {
        setManualTagsSaving(false)
      }
    },
    [
      actorEmail,
      actorName,
      candidate?.email,
      candidate?.phone,
      candidateApplicationData?.vacancy?.locatie,
      candidateApplicationData?.vacancy?.vakgebied,
      candidateHubspotId,
      companyHubspotId,
      fullName,
      setApplicationTags,
      vacancyCompany,
      vacancyHubspotId,
      vacancyTitle,
    ]
  )

  useEffect(() => {
    if (!candidateApplicationData) {
      return
    }

    const [primaryFeature] = candidateApplicationFeatures
    setError(null)

    if (primaryFeature) {
      setStatus(primaryFeature.column)
      setActiveFeature((current) => {
        if (!current) {
          return primaryFeature
        }
        const shouldUpdate =
          current.column !== primaryFeature.column ||
          current.statusLabel !== primaryFeature.statusLabel ||
          (current.applicationId ?? null) !== (primaryFeature.applicationId ?? null) ||
          (current.vacancyId ?? null) !== (primaryFeature.vacancyId ?? null) ||
          (current.owner?.id ?? null) !== (primaryFeature.owner?.id ?? null) ||
          (current.owner?.name ?? null) !== (primaryFeature.owner?.name ?? null) ||
          (current.associationTypeId ?? null) !==
          (primaryFeature.associationTypeId ?? null)
        if (!shouldUpdate) {
          return current
        }
        return {
          ...current,
          column: primaryFeature.column,
          statusLabel: primaryFeature.statusLabel,
          associationTypeId:
            primaryFeature.associationTypeId ?? current.associationTypeId ?? null,
          applicationId: primaryFeature.applicationId ?? current.applicationId ?? null,
          vacancyId: primaryFeature.vacancyId ?? current.vacancyId ?? null,
          owner: primaryFeature.owner
            ? { ...primaryFeature.owner }
            : current.owner,
        }
      })
      setVacancyTitle((current) =>
        current ??
        candidateApplicationData.vacancy?.title ??
        candidateApplicationData.vacancy?.vacatureNaam ??
        null
      )
      setVacancyCompany((current) => current ?? candidateApplicationData.company?.name ?? null)
    }

    const primaryCandidate = candidateApplicationData.applications[0]?.candidate ?? null
    if (!primaryCandidate) {
      return
    }

    setCandidate((previous) => {
      const next: PersonalInformationCandidate = { ...(previous ?? {}) }

      const assignField = <K extends keyof PersonalInformationCandidate>(
        key: K,
        value: string | null | undefined
      ) => {
        const normalized = value ?? null
        if (!normalized) {
          return
        }
        const currentValue = next[key]
        const isEmpty =
          currentValue === null ||
          currentValue === undefined ||
          (typeof currentValue === "string" && currentValue.trim().length === 0)
        if (isEmpty) {
          next[key] = normalized
        }
      }

      const name = primaryCandidate.name?.trim() ?? ""
      if (name) {
        const parts = name.split(/\s+/)
        const [first, ...rest] = parts
        assignField("firstName", first ?? null)
        assignField("lastName", rest.length > 0 ? rest.join(" ") : null)
      }

      assignField("email", primaryCandidate.email ?? null)
      assignField("phone", primaryCandidate.phone ?? primaryCandidate.mobilePhone ?? null)
      assignField("city", primaryCandidate.city ?? null)
      assignField("street", primaryCandidate.street ?? null)
      assignField("postalCode", primaryCandidate.postalCode ?? null)
      assignField("resumeUrl", primaryCandidate.cv ?? null)
      assignField("birthDate", primaryCandidate.birthDate ?? null)
      return next
    })

    setFullName((prev) => {
      if (prev && prev.trim().length > 0 && prev !== "Kandidaat") {
        return prev
      }
      const candidateName = primaryCandidate.name?.trim()
      if (candidateName && candidateName.length > 0) {
        return candidateName
      }
      if (primaryCandidate.email && primaryCandidate.email.trim().length > 0) {
        return primaryCandidate.email
      }
      return prev
    })
    setLoading(false)
  }, [
    candidateApplicationData,
    candidateApplicationFeatures,
  ])

  const candidateAttachments = useMemo(
    () =>
      Array.isArray(contactAttachmentsData?.attachments)
        ? contactAttachmentsData.attachments.filter((attachment) => {
            if (!attachment || typeof attachment !== "object") {
              return false
            }
            const id = typeof attachment.id === "string" ? attachment.id.trim() : ""
            const url = typeof attachment.url === "string" ? attachment.url.trim() : ""
            return id.length > 0 && url.length > 0
          })
        : [],
    [contactAttachmentsData?.attachments]
  )

  useEffect(() => {
    const cvAttachment = candidateAttachments.find(
      (attachment) => attachment.source === "cv" && attachment.url
    )

    if (!cvAttachment) {
      return
    }

    setCandidate((previous) => {
      if (!previous) {
        return previous
      }
      if (previous.resumeUrl && previous.resumeUrl.trim().length > 0) {
        return previous
      }
      return {
        ...previous,
        resumeUrl: cvAttachment.url,
      }
    })
  }, [candidateAttachments])

  const contactAttachmentsErrorMessage = useMemo(() => {
    if (!contactAttachmentsError) {
      return null
    }
    if (contactAttachmentsError instanceof Error) {
      return contactAttachmentsError.message
    }
    if (typeof contactAttachmentsError === "string") {
      return contactAttachmentsError
    }
    return "Kon bijlagen niet laden."
  }, [contactAttachmentsError])


  const mapHubspotStatusToCandidateStatus = useCallback(
    (status?: string | null, statusLabel?: string | null): CandidateStatusId => {
      const normalizedStatus = (status ?? "").toLowerCase()
      const normalizedLabel = (statusLabel ?? "").toLowerCase()

      const slugToStage: Record<string, CandidateStatusId> = {
        gesolliciteerd: "sollicitanten",
        sollicitanten: "sollicitanten",
        uitnodigen: "uitnodigingen",
        "gesprek-sw": "gesprek-sw",
        "gesprek s&w": "gesprek-sw",
        "gesprek-opdrachtgever": "gesprek-opdrachtgever",
        assessment: "assessment",
        aangenomen: "aangenomen",
        reject: "afgewezen",
        afgewezen: "afgewezen",
      }

      if (normalizedStatus && slugToStage[normalizedStatus]) {
        return slugToStage[normalizedStatus]
      }

      if (normalizedLabel) {
        const labelKey = normalizedLabel.replace(/\s+/g, " ")
        if (slugToStage[labelKey]) {
          return slugToStage[labelKey]
        }
        const labelSlug = labelKey.replace(/\s+/g, "-")
        if (slugToStage[labelSlug]) {
          return slugToStage[labelSlug]
        }
        if (labelKey.includes("gesprek") && labelKey.includes("opdrachtgever")) {
          return "gesprek-opdrachtgever"
        }
        if (labelKey.includes("gesprek") && labelKey.includes("s&w")) {
          return "gesprek-sw"
        }
      }

      return "sollicitanten"
    },
    []
  )

  type VacancySidebarInfo = {
    title: string | null
    vacatureNaam: string | null
    company: string | null
    location: string | null
    vakgebied: string | null
    status: CandidateStatusId | null
    statusLabel: string | null
    createdAt: string | null
    appliedAt: string | null
  }

  const vacancyInfoById = useMemo(() => {
    const map = new Map<string, VacancySidebarInfo>()

    const mergeInfo = (
      rawId: string | number | null | undefined,
      info: Partial<VacancySidebarInfo>
    ) => {
      const vacancyId = normalizeHubspotId(rawId)
      if (!vacancyId) {
        return
      }
      const previous =
        map.get(vacancyId) ??
        {
          title: null,
          vacatureNaam: null,
          company: null,
          location: null,
          vakgebied: null,
          status: null,
          statusLabel: null,
          createdAt: null,
          appliedAt: null,
        }

      const next = { ...previous }

      for (const [key, value] of Object.entries(info)) {
        if (value === undefined || value === null) continue
        const normalized = typeof value === "string" ? value.trim() : value
        if (typeof normalized === "string" && normalized.length === 0) {
          continue
        }
        next[key as keyof VacancySidebarInfo] = normalized as never
      }

      map.set(vacancyId, next)
    }

    if (candidateApplicationData?.vacancy?.id) {
      mergeInfo(candidateApplicationData.vacancy.id, {
        title: candidateApplicationData.vacancy.title ?? null,
        vacatureNaam: candidateApplicationData.vacancy.vacatureNaam ?? null,
        location: candidateApplicationData.vacancy.locatie ?? null,
        vakgebied: candidateApplicationData.vacancy.vakgebied ?? null,
        createdAt: candidateApplicationData.vacancy.createdAt ?? null,
        company:
          candidateApplicationData.company?.name ?? vacancyCompany ?? null,
      })
    }

    for (const application of candidateApplicationData?.applications ?? []) {
      mergeInfo(application.vacancyId ?? candidateApplicationData?.vacancy?.id ?? null, {
        status: mapHubspotStatusToCandidateStatus(application.status, application.statusLabel),
        statusLabel: application.statusLabel ?? application.status ?? null,
        appliedAt: application.appliedAt ?? null,
      })
    }

    for (const vacancy of candidateApplicationData?.vacancies ?? []) {
      mergeInfo(vacancy.vacancyId, {
        title: vacancy.title ?? vacancy.vacatureNaam ?? null,
        vacatureNaam: vacancy.vacatureNaam ?? null,
        location: vacancy.locatie ?? null,
        vakgebied: vacancy.vakgebied ?? null,
        company: vacancy.company ?? null,
        createdAt: vacancy.createdAt ?? null,
        appliedAt: vacancy.appliedAt ?? null,
        status: mapHubspotStatusToCandidateStatus(vacancy.status ?? null, vacancy.statusLabel ?? null),
        statusLabel: vacancy.statusLabel ?? vacancy.status ?? null,
      })
    }

    if (activeFeature?.vacancyId || fallbackVacancyId) {
      mergeInfo(activeFeature?.vacancyId ?? fallbackVacancyId, {
        company: activeFeature?.owner?.name ?? vacancyCompany ?? null,
        status: activeFeature?.column ?? null,
        statusLabel: activeFeature?.statusLabel ?? null,
      })
    }

    for (const feature of candidateApplicationFeatures) {
      mergeInfo(feature.vacancyId ?? fallbackVacancyId, {
        company: feature.owner?.name ?? null,
        status: feature.column,
        statusLabel: feature.statusLabel ?? null,
      })
    }

    if (!map.size && fallbackVacancyId) {
      mergeInfo(fallbackVacancyId, {
        title: vacancyTitle ?? null,
        company: vacancyCompany ?? null,
      })
    }

    return map
  }, [
    candidateApplicationData?.applications,
    candidateApplicationData?.vacancies,
    candidateApplicationData?.vacancy,
    candidateApplicationData?.company?.name,
    candidateApplicationFeatures,
    activeFeature?.vacancyId,
    activeFeature?.owner?.name,
    activeFeature?.column,
    activeFeature?.statusLabel,
    fallbackVacancyId,
    vacancyCompany,
    vacancyTitle,
    mapHubspotStatusToCandidateStatus,
  ])

  const candidateVacancies = useMemo<CandidateVacancyAssignment[]>(() => {
    const relatedVacancies = candidateApplicationData?.vacancies ?? []
    if (!relatedVacancies.length) {
      return []
    }

    const assignments: CandidateVacancyAssignment[] = []
    const seenVacancyIds = new Set<string>()

    for (const vacancy of relatedVacancies) {
      const vacancyId = normalizeHubspotId(vacancy.vacancyId)
      if (!vacancyId || seenVacancyIds.has(vacancyId)) {
        continue
      }
      seenVacancyIds.add(vacancyId)

      const info = vacancyInfoById.get(vacancyId)
      const title =
        info?.vacatureNaam ??
        info?.title ??
        vacancy.vacatureNaam ??
        vacancy.title ??
        "Vacature"
      const company =
        info?.company ?? vacancy.company ?? vacancyCompany ?? "Onbekende opdrachtgever"
      const location = info?.location ?? vacancy.locatie ?? null
      const vakgebied = info?.vakgebied ?? vacancy.vakgebied ?? null
      const status =
        info?.status ??
        mapHubspotStatusToCandidateStatus(vacancy.status ?? null, vacancy.statusLabel ?? null)
      const statusLabel = info?.statusLabel ?? vacancy.statusLabel ?? null

      assignments.push({
        id: `related-${vacancyId}`,
        vacancyId,
        title,
        company,
        location,
        vakgebied,
        status,
        statusLabel,
        applicationId: null,
      })
    }

    return assignments
  }, [
    candidateApplicationData?.vacancies,
    vacancyInfoById,
    vacancyCompany,
    mapHubspotStatusToCandidateStatus,
  ])

  const primaryApplicationId = useMemo(() => {
    if (!candidateApplicationData?.applications || candidateApplicationData.applications.length === 0) {
      return candidateApplicationFeatures[0]?.applicationId ?? null
    }

    const matched =
      candidateApplicationData.applications.find((application) => {
        const normalized = normalizeHubspotId(application.vacancyId)
        return normalized && vacancyHubspotId && normalized === vacancyHubspotId
      }) ?? candidateApplicationData.applications[0]

    return matched?.applicationId ?? candidateApplicationFeatures[0]?.applicationId ?? null
  }, [candidateApplicationData?.applications, candidateApplicationFeatures, vacancyHubspotId])
  const primaryApplication = useMemo(() => {
    if (!candidateApplicationData?.applications || candidateApplicationData.applications.length === 0) {
      return null
    }
    if (!primaryApplicationId) {
      return candidateApplicationData.applications[0]
    }
    return (
      candidateApplicationData.applications.find(
        (application) => application.applicationId === primaryApplicationId
      ) ?? candidateApplicationData.applications[0]
    )
  }, [candidateApplicationData?.applications, primaryApplicationId])
  const combinedPrimaryTags = useMemo(
    () => combineAssociationTags(primaryApplication?.tagsJson, primaryApplication?.tags),
    [primaryApplication?.tagsJson, primaryApplication?.tags]
  )
  const manualTags = useMemo(
    () => combinedPrimaryTags.map((tag) => tag.label),
    [combinedPrimaryTags]
  )
  const manualTagsKey = manualTags.join("|")
  const [localManualTags, setLocalManualTags] = useState<string[]>(manualTags)
  useEffect(() => {
    setLocalManualTags(manualTags)
  }, [manualTagsKey])

  const timelineItems = useMemo<TimelineItem[]>(() => {
    const events = candidateApplicationData?.events ?? []
    if (!events.length) {
      return []
    }

    return events
      .filter((event) => {
        if (!primaryApplicationId) {
          return true
        }
        return event.application_id === primaryApplicationId
      })
      .map((event) => {
        const afterStatusLabel =
          extractString(event.after, "statusLabel") ?? extractString(event.after, "status")
        const beforeStatusLabel =
          extractString(event.before, "statusLabel") ?? extractString(event.before, "status")

        let heading: string
        let subtext: string | undefined

        switch (event.event_type) {
          case "status_changed":
            heading = afterStatusLabel
              ? `Status gewijzigd naar ${afterStatusLabel}`
              : "Status gewijzigd"
            if (beforeStatusLabel && afterStatusLabel) {
              subtext = `Van ${beforeStatusLabel} naar ${afterStatusLabel}`
            } else if (beforeStatusLabel) {
              subtext = `Vorige status: ${beforeStatusLabel}`
            }
            break
          case "email_sent":
            heading = "E-mail verstuurd"
            subtext = undefined
            break
          case "email_queued":
            heading = "E-mail ingepland"
            subtext = undefined
            break
          case "note_added":
            heading = "Notitie toegevoegd"
            subtext = event.notes ?? "Notitie toegevoegd aan deze kandidaat."
            break
          case "error":
            heading = "Foutmelding"
            subtext = event.notes ?? "Er trad een fout op tijdens de synchronisatie."
            break
          case "webhook_sync":
            heading = "Synchronisatie afgerond"
            subtext = event.notes ?? "Gegevens zijn gesynchroniseerd."
            break
          case "ui_action":
            heading = "Handmatige actie"
            subtext = event.notes ?? "Actie via de interface geregistreerd."
            break
          default:
            heading = event.event_type
            subtext = event.notes ?? undefined
            break
        }

        const sourceLabel =
          typeof event.source === "string" && event.source.trim().length > 0
            ? event.source.trim().toLowerCase() === "ui"
              ? "Onbekende gebruiker"
              : event.source.trim()
            : "Systeem"
        const performedBy =
          (event.actor_email && event.actor_email.trim().length > 0 && event.actor_email) ||
          (event.actor_name && event.actor_name.trim().length > 0 && event.actor_name) ||
          sourceLabel

        return {
          id: `event-${event.id}`,
          timestamp: event.ts,
          heading,
          subtext,
          performedBy,
        }
      })
  }, [candidateApplicationData?.events, primaryApplicationId])

  const TIMELINE_PAGE_SIZE = 4
  const [timelinePage, setTimelinePage] = useState(0)

  const totalTimelinePages = useMemo(() =>
    Math.max(1, Math.ceil(timelineItems.length / TIMELINE_PAGE_SIZE)),
    [timelineItems.length]
  )

  useEffect(() => {
    setTimelinePage(0)
  }, [primaryApplicationId])

  useEffect(() => {
    setTimelinePage((current) => {
      const maxIndex = Math.max(0, totalTimelinePages - 1)
      return current > maxIndex ? maxIndex : current
    })
  }, [totalTimelinePages])

  const paginatedTimelineItems = useMemo(() => {
    const start = timelinePage * TIMELINE_PAGE_SIZE
    return timelineItems.slice(start, start + TIMELINE_PAGE_SIZE)
  }, [timelineItems, timelinePage])

  const candidateApplicationErrorMessage = useMemo(() => {
    if (!candidateApplicationError) {
      return null
    }
    if (candidateApplicationError instanceof Error) {
      return candidateApplicationError.message
    }
    if (typeof candidateApplicationError === "string") {
      return candidateApplicationError
    }
    return "Kon de kandidaatapplicatie niet ophalen."
  }, [candidateApplicationError])

  const displayedError = error ?? candidateApplicationErrorMessage

  useEffect(() => {
    if (vacancyTitle && vacancyCompany) {
      return
    }

    if (candidateApplicationData?.vacancy || candidateApplicationData?.company) {
      return
    }

    const targetVacancyId =
      vacancyParam ??
      locationState?.vacancyId ??
      activeFeature?.vacancyId ??
      applicationContext?.vacancyId ??
      fallbackVacancyId ??
      null

    if (!targetVacancyId) {
      return
    }

    let isCancelled = false

    const fetchVacancyContext = async () => {
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
        if (!supabaseUrl) {
          console.warn("[candidate] Supabase URL ontbreekt; vacaturegegevens worden overgeslagen.")
          return
        }
        const query = new URLSearchParams({
          vacancy: String(targetVacancyId),
          syncSnapshot: "false",
        })
        const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
        const headers: Record<string, string> = { "Content-Type": "application/json" }
        if (anonKey) {
          headers.apikey = anonKey
          headers.Authorization = `Bearer ${anonKey}`
        }
        const accessToken = await getAccessToken()
        headers["x-auth-token"] = `Bearer ${accessToken}`
        const response = await fetch(
          `${supabaseUrl}/functions/v1/get-vacancy-applications?${query.toString()}`,
          {
            method: "GET",
            headers,
            credentials: "omit",
            cache: "no-store",
          }
        )

        if (!response.ok) {
          throw new Error(`vacancy fetch failed with status ${response.status}`)
        }

        const payload = await response.json()
        if (isCancelled) {
          return
        }

        setVacancyTitle((current) =>
          current ?? payload?.vacancy?.title ?? payload?.vacancy?.vacatureNaam ?? null
        )
        setVacancyCompany((current) => current ?? payload?.company?.name ?? null)
      } catch (error) {
        if (!isCancelled) {
          console.warn("[candidate] vacancy context fetch failed:", error)
        }
      }
    }

    fetchVacancyContext()

    return () => {
      isCancelled = true
    }
  }, [
    vacancyTitle,
    vacancyCompany,
    vacancyParam,
    locationState?.vacancyId,
    activeFeature?.vacancyId,
    applicationContext?.vacancyId,
    candidateApplicationData?.vacancy?.id,
    candidateApplicationData?.company?.id,
    getAccessToken,
  ])

  const isLoadingDetails = loading || candidateApplicationLoading
  // const phoneNumber = candidate?.phone ?? null
  const resumeUrl = useMemo(() => {
    const url = candidate?.resumeUrl?.trim()
    if (!url) {
      return null
    }
    if (url.toLowerCase() === "undefined") {
      return null
    }
    const match = url.match(/\/files\/([^/]+)\/signed-url-redirect/i)
    const fileId = match?.[1]?.trim() ?? ""
    if (!fileId) {
      return null
    }
    return url
  }, [candidate?.resumeUrl])
  const hubspotQuery =
    candidate?.email ?? (fullName && fullName !== "Kandidaat" ? fullName : null)
  const hubspotPortalId = import.meta.env.VITE_HS_PORTAL_ID
  const hubspotRecordUrl =
    hubspotPortalId && candidateId
      ? `https://app-eu1.hubspot.com/contacts/${hubspotPortalId}/record/0-1/${candidateId}`
      : null
  const hubspotUrl =
    candidate?.hubspotUrl ??
    hubspotRecordUrl ??
    (hubspotQuery
      ? `https://app.hubspot.com/contacts/list/contacts/0/?query=${encodeURIComponent(
        hubspotQuery
      )}`
      : null)

  const vacancyLinkTarget = vacancySlug ? `/vacature/${vacancySlug}` : null

  let mainContent: JSX.Element | null = null

  const skeletonMainContent = (
    <div className="space-y-6" role="status" aria-live="polite">
      <div className="h-24 w-full rounded-xl skeleton-shimmer" aria-hidden="true" />
      <div className="h-[360px] w-full rounded-xl skeleton-shimmer" aria-hidden="true" />
      <div className="h-56 w-full rounded-xl skeleton-shimmer" aria-hidden="true" />
      <span className="sr-only">Paginaonderdelen worden geladen</span>
    </div>
  )

  if (displayedError) {
    mainContent = (
      <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
        {displayedError}
      </div>
    )
  } else if (isLoadingDetails) {
    mainContent = skeletonMainContent
  } else if (candidate) {
    mainContent = (
      <>
        <CandidateVacancyBanner
          vacancyTitle={vacancyTitle}
          vacancyCompany={vacancyCompany}
          vacancyUrl={vacancyLinkTarget}
        />
        <PersonalInformation
          candidate={candidate}
          emptyStateMessage="Geen kandidaatgegevens beschikbaar."
          className="bg-background px-6 py-6"
        />
        <CandidateAttachments
          attachments={candidateAttachments}
          loading={contactAttachmentsLoading}
          error={contactAttachmentsErrorMessage}
          onRetry={() => {
            void refetchContactAttachments()
          }}
        />
        <Timeline
          className="mt-2"
          items={paginatedTimelineItems}
          controls={
            timelineItems.length > TIMELINE_PAGE_SIZE ? (
              <ButtonGroup>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setTimelinePage((page) => Math.max(0, page - 1))}
                  disabled={timelinePage === 0}
                  aria-label="Vorige pagina"
                  className="h-8 w-8"
                >
                  <ChevronLeft className="size-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    setTimelinePage((page) =>
                      page + 1 >= totalTimelinePages ? page : page + 1
                    )
                  }
                  disabled={timelinePage + 1 >= totalTimelinePages}
                  aria-label="Volgende pagina"
                  className="h-8 w-8"
                >
                  <ChevronRight className="size-4" />
                </Button>
              </ButtonGroup>
            ) : null
          }
        />
      </>
    )
  } else {
    mainContent = (
      <div className="rounded-md border border-dashed border-border px-6 py-10 text-sm text-slate-500">
        Geen kandidaatgegevens gevonden.
      </div>
    )
  }

  const sidebarSkeleton = (
    <div className="space-y-4 rounded-lg border border-dashed border-slate-200 p-6" role="status" aria-live="polite">
      <div className="h-6 w-24 rounded-md skeleton-shimmer" aria-hidden="true" />
      <div className="h-10 w-full rounded-md skeleton-shimmer" aria-hidden="true" />
      <div className="h-10 w-full rounded-md skeleton-shimmer" aria-hidden="true" />
      <div className="h-10 w-full rounded-md skeleton-shimmer" aria-hidden="true" />
      <span className="sr-only">Zijkolom wordt geladen</span>
    </div>
  )

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-6xl flex-col gap-6 px-6 py-10">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-6">
          <Button
            type="button"
            variant="ghost"
            className="flex items-center gap-2 px-0 text-sm text-slate-500 hover:text-foreground rounded-full border border-slate-200 size-12 cursor-pointer duration-300 ease-in-out hover:bg-primary hover:text-white"
            onClick={() =>
              backDestination ? navigate(`/vacature/${backDestination}`) : navigate(-1)
            }
            aria-label="Terug naar vorige pagina"
          >
            <Undo2 className="size-4" />
          </Button>
          {isLoadingDetails ? (
            <div className="flex flex-col gap-2" role="status" aria-live="polite">
              <span className="h-8 w-40 rounded-md skeleton-shimmer" aria-hidden="true" />
              {candidateId ? (
                <span className="h-4 w-28 rounded-md skeleton-shimmer" aria-hidden="true" />
              ) : null}
              <span className="sr-only">Kandidaatgegevens worden geladen</span>
            </div>
          ) : (
            <header className="flex flex-col gap-1">
              <h1 className="text-3xl font-semibold">{fullName}</h1>
              {candidateId ? (
                <p className="text-sm text-slate-500">Kandidaat ID: {candidateId}</p>
              ) : null}
            </header>
          )}
        </div>

        {isLoadingDetails ? (
          <div className="flex flex-wrap items-center gap-5" role="status" aria-live="polite">
            <span className="h-12 w-24 rounded-full skeleton-shimmer" aria-hidden="true" />
            <span className="h-12 w-28 rounded-full skeleton-shimmer" aria-hidden="true" />
            <span className="h-12 w-32 rounded-full skeleton-shimmer" aria-hidden="true" />
            <span className="sr-only">Actieknoppen worden geladen</span>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            {resumeUrl ? (
              <Button asChild variant="outline" size="lg">
                <a
                  href={resumeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="flex items-center gap-2"
                >
                  <FileDown className="size-4" aria-hidden />
                  Download CV
                </a>
              </Button>
            ) : (
               <Button asChild variant="disabled" size="lg">
                  <span>
                    <FileDown className="size-4" aria-hidden />
                    geen CV beschikbaar
                  </span>
                </Button>
            )}
            {hubspotUrl ? (
              <Button asChild size="lg">
                <a
                  href={hubspotUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-white"
                >
                  <ExternalLink className="size-4" aria-hidden />
                  Open in Hubspot
                </a>
              </Button>
            ) : null}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <div className="flex-1 space-y-6">{mainContent}</div>
        <div className="flex w-full flex-col gap-3 lg:max-w-xs lg:flex-shrink-0">
          {isLoadingDetails ? (
            sidebarSkeleton
          ) : (
            <CandidateSidebar
              status={status}
              onStatusChange={handleStatusChange}
              vacancies={candidateVacancies}
              vacanciesLoading={candidateApplicationLoading}
              candidateName={fullName}
              candidateEmail={candidate?.email ?? null}
              candidateId={candidateHubspotId ?? candidateId ?? null}
              applicationId={
                applicationIdFromState ??
                activeFeature?.applicationId ??
                primaryApplicationId ??
                null
              }
              candidateApplication={candidateApplicationData}
              manualTags={localManualTags}
              onManualTagsSave={handleManualTagsSave}
              manualTagsSaving={manualTagsSaving}
              manualTagsError={manualTagsError}
            />
          )}
          {statusSaving ? (
            <p className="text-sm text-slate-500" aria-live="polite">
              Status wordt bijgewerkt…
            </p>
          ) : null}
          {statusError ? (
            <p className="text-sm text-red-500" aria-live="assertive">
              {statusError}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default Kandidaat
